import api from './api';
import { AxiosResponse } from 'axios';

import {
  PhysicalUnit,
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DensityPhysicalUnit,
  DynamicViscosityPhysicalUnit,
  SpecificHeatCapacityPhysicalUnit,
  ThermalConductivityPhysicalUnit,
  SpecificThermalResistancePhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVelocityPhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,
  OptionalHeatTransferCoefficientPhysicalUnit,
  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit,
  TemperatureDifferencePhysicalUnit
} from '../components/common/types/physicalUnits';

// Re-export physical unit types for use in other components
export type {
  PhysicalUnit,
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DensityPhysicalUnit,
  DynamicViscosityPhysicalUnit,
  SpecificHeatCapacityPhysicalUnit,
  ThermalConductivityPhysicalUnit,
  SpecificThermalResistancePhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVelocityPhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,
  OptionalHeatTransferCoefficientPhysicalUnit,
  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit,
  TemperatureDifferencePhysicalUnit
};

// Type definitions
export interface CondenserFluidsAPI {
  refrigerant: string;
  medium: MediumAPI;
  fouling_factor_inside: SpecificThermalResistancePhysicalUnit;
  fouling_factor_outside: SpecificThermalResistancePhysicalUnit;
}

export interface MediumAPI {
  name: string;
  brine_concentration: RatioPhysicalUnit;
  custom_name?: string | null;
  reference_temperature?: OptionalTemperaturePhysicalUnit | null;
  custom_density?: OptionalDensityPhysicalUnit | null;
  custom_dynamic_viscosity?: OptionalDynamicViscosityPhysicalUnit | null;
  custom_specific_heat_capacity?: OptionalSpecificHeatCapacityPhysicalUnit | null;
  custom_thermal_conductivity?: OptionalThermalConductivityPhysicalUnit | null;
}

export interface WKCOperatingConditionsAPI {
  sub_cooling: TemperatureDifferencePhysicalUnit;
  temperature_evaporation?: OptionalTemperaturePhysicalUnit | null;
  temperature_suction_gas?: OptionalTemperaturePhysicalUnit | null;
  temperature_superheated_vapour?: OptionalTemperaturePhysicalUnit | null;
  override_temperature_superheated_vapour: boolean;
}

export interface InterChangableOperatingConditionsWKCAPI {
  temperature_medium_inlet?: OptionalTemperaturePhysicalUnit | null;
  temperature_medium_outlet?: OptionalTemperaturePhysicalUnit | null;
  capacity?: OptionalThermalPowerPhysicalUnit | null;
  volumetric_flow_rate?: OptionalVolumetricFlowRatePhysicalUnit | null;
  temperature_saturation_condensation?: OptionalTemperaturePhysicalUnit | null;
}

export interface RequestBodyWKCHeatExchangerAPI {
  heat_exchanger_name: string;
  fluids: CondenserFluidsAPI;
  system_of_units: string;
  operating_conditions: WKCOperatingConditionsAPI;
  interchangable_input_conditions?: InterChangableOperatingConditionsWKCAPI | null;
  calculation_mode: string;
}

export interface OptionalRequestBodyWKCHeatExchanger extends Partial<RequestBodyWKCHeatExchangerAPI> {
  system_of_units: string;  // This field is required even in the optional version
}

export interface RequestBodySelectWKCHeatExchangerAPI {
  heat_exchanger_name?: string | null;
  fluids: CondenserFluidsAPI;
  system_of_units: string;
  operating_conditions: WKCOperatingConditionsAPI;
  interchangable_input_conditions?: InterChangableOperatingConditionsWKCAPI | null;
  calculation_mode: string;
  selected_models: string[];
}

export interface Message {
  message_level: string;
  message_content: string;
}

// Add new interfaces for medium, refrigerant and dimensions results
export interface MediumResultAPI {
  name: string;
  mean_temperature: OptionalTemperaturePhysicalUnit | null;
  thermal_conductivity: OptionalThermalConductivityPhysicalUnit | null;
  density: OptionalDensityPhysicalUnit | null;
  dynamic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  kinematic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  specific_heat_capacity: OptionalSpecificHeatCapacityPhysicalUnit | null;
  prandtl_number: number | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
}

export interface RefrigerantResultAPI {
  name: string;
  saturation_pressure: OptionalPressurePhysicalUnit | null;
  critical_temperature: OptionalTemperaturePhysicalUnit | null;
  critical_pressure: OptionalPressurePhysicalUnit | null;
}

export interface SelectionLabelsCondenserCoaxial {
  calculation_mode: string[];
  unit_system: string[];
  message_level: string[];
  refrigerant: string[];
  ratio_unit: string[];
  medium_type: string[];
  thermal_power_unit: string[];
  pressure_unit: string[];
  velocity_unit: string[];
  volumetric_flow_unit: string[];
  thermal_resistance_unit: string[];
  temperature_unit: string[];
  temperature_difference_unit: string[];
  heattransfer_coefficient_unit: string[];
  length_unit: string[];
  oil_viscosity: string[];
  heatflux_unit: string[];
  specific_heat_capacity_unit: string[];
  dynamic_viscosity_unit: string[];
  density_unit: string[];
  thermal_conductivity_unit: string[];
}

export interface HeatExchangerDimensionsAPI {
  install_dimension_a: LengthPhysicalUnit | null;
  install_dimension_b: LengthPhysicalUnit | null;
  install_dimension_h: LengthPhysicalUnit | null;
  connection_dimension_d1: LengthPhysicalUnit | null;
  connection_dimension_d2: LengthPhysicalUnit | null;
  connection_dimension_d3: LengthPhysicalUnit | null;
  connection_dimension_a: LengthPhysicalUnit | null;
  connection_dimension_h: LengthPhysicalUnit | null;
  volume_outside_medium: VolumetricFlowRatePhysicalUnit | null;
  volume_inside_medium: VolumetricFlowRatePhysicalUnit | null;
  weight: LengthPhysicalUnit | null;
  availability?: string;
  PED?: string;
}

export interface ResultBodyWKCHeatExchangerAPI {
  input_data?: any | null;
  messages?: Message[] | null;
  overall_message_level?: string | null;
  capacity?: ThermalPowerPhysicalUnit | null;
  volumetric_flow_rate?: VolumetricFlowRatePhysicalUnit | null;
  overall_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inside_pressure_drop?: PressurePhysicalUnit | null;
  inside_pressure_drop_total?: PressurePhysicalUnit | null;
  inside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inside_temperature_mean?: TemperaturePhysicalUnit | null;
  inside_velocity?: VelocityPhysicalUnit | null;
  logarithmic_mean_temperature_difference?: TemperatureDifferencePhysicalUnit | null;
  outside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  outside_heat_flux?: ThermalPowerPhysicalUnit | null;
  over_design?: RatioPhysicalUnit | null;
  inside_fouling_resistance?: RatioPhysicalUnit | null;
  outside_fouling_resistance?: RatioPhysicalUnit | null;
  inside_htc_resistance?: RatioPhysicalUnit | null;
  outside_htc_resistance?: RatioPhysicalUnit | null;
  wall_resistance?: RatioPhysicalUnit | null;
  reynolds_number?: number | null;
  temperature_medium_inlet?: TemperaturePhysicalUnit | null;
  temperature_medium_outlet?: TemperaturePhysicalUnit | null;
  temperature_saturation_condensation?: TemperaturePhysicalUnit | null;
  tube_length?: LengthPhysicalUnit | null;
  temperature_superheated_vapour?: TemperaturePhysicalUnit | null;
  tube_count?: number | null;
  coil_diameter?: LengthPhysicalUnit | null;
  mantle_diameter?: LengthPhysicalUnit | null;
  sub_cooling?: TemperatureDifferencePhysicalUnit | null;
  material?: string | null;
  medium?: MediumResultAPI | null;
  refrigerant?: RefrigerantResultAPI | null;
  heat_exchanger_dimensions?: HeatExchangerDimensionsAPI | null;
}

export interface ExtendedResultBodyWKCHeatExchangerAPI extends ResultBodyWKCHeatExchangerAPI {
  timestamp?: string;
}

/**
 * Comprehensive service for the Coaxial Condenser calculator
 * Combines both UI and calculation functionality
 */
const coaxialCondenserService = {
  // ======================================================
  // Calculation API Endpoints
  // ======================================================
  
  /**
   * Performs the condenser calculation
   * @param data Calculation input data
   */
  calculateCondenser: (data: RequestBodyWKCHeatExchangerAPI): Promise<AxiosResponse<ResultBodyWKCHeatExchangerAPI>> => 
    api.post<ResultBodyWKCHeatExchangerAPI>('/v1/heat-exchanger/coaxial-condenser/model/calculate', data),

  /**
   * Selects and calculates results for multiple WKC heat exchanger models
   * @param data Calculation input data
   */
  selectCondensers: (data: RequestBodySelectWKCHeatExchangerAPI): Promise<AxiosResponse<ResultBodyWKCHeatExchangerAPI[]>> => 
    api.post<ResultBodyWKCHeatExchangerAPI[]>('/v1/heat-exchanger/coaxial-condenser/model/select', data),

  // ======================================================
  // UI API Endpoints
  // ======================================================

  /**
   * Gets the input labels and dropdown options for the UI
   */
  getInputLabels: (): Promise<AxiosResponse<SelectionLabelsCondenserCoaxial>> =>
    api.get('/v1/heat-exchanger/coaxial-condenser/model/ui/input-labels'),

  /**
   * Gets the list of available refrigerants for WKC heat exchangers
   */
  getRefrigerants: (): Promise<AxiosResponse<string[]>> =>
    api.get('/v1/heat-exchanger/coaxial-condenser/model/ui/refrigerants'),

  /**
   * Converts values in the request body to the specified unit system
   * @param data Request body to convert
   */
  convertValues: (data: OptionalRequestBodyWKCHeatExchanger): Promise<AxiosResponse<OptionalRequestBodyWKCHeatExchanger>> =>
    api.post('/v1/heat-exchanger/coaxial-condenser/model/ui/convert-values', data),

  // ======================================================
  // Export API Endpoints
  // ======================================================
  
  /**
   * Generates Excel results file
   * @param results Results to export
   * @param name Optional filename
   */
  generateExcelResults: (
    results: ResultBodyWKCHeatExchangerAPI[], 
    name: string = 'coaxial_condenser_results'
  ): Promise<AxiosResponse<Blob>> => 
    api.post<Blob>(
      '/v1/heat-exchanger/coaxial-condenser/model/ui/download/results-excel-file',
      results,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    ),

  /**
   * Generates a PDF report
   * @param result Result to generate PDF for
   * @param name Optional filename
   * @param share_url Optional share URL generated by frontend
   * @param project_info Optional project information
   */
  generatePDF: (result: ResultBodyWKCHeatExchangerAPI, name: string = 'coaxial_condenser_results', share_url?: string, project_info?: any): Promise<AxiosResponse<Blob>> => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    
    const payload = {
      result,
      project_info,
      share_url
    };
    
    return api.post<Blob>(
      `/v1/heat-exchanger/coaxial-condenser/model/ui/download/pdf?${params.toString()}`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
  },

  convertTDUFile: (tduData: any): Promise<AxiosResponse<OptionalRequestBodyWKCHeatExchanger>> => 
    api.post<OptionalRequestBodyWKCHeatExchanger>('/v1/heat-exchanger/coaxial-condenser/model/ui/convert-tdu', tduData),
};

export default coaxialCondenserService; 